// Vendor libraries
// Base Classes
import { FolderableMoveStrategy } from './folderable-move-strategy';
// Typing
import { FolderableType } from 'FoldersApp/enums/folderable-type.enum';
import { ToastMessageType } from 'NewAngular/features/components/toast-messages/toast-messages.enum';
export class FolderableIndividualMoveStrategy extends FolderableMoveStrategy {
    constructor(folderableDirective) {
        super(folderableDirective);
        this.SELECTION_GHOST_IMAGE_ID = 'individual-selection-ghost-image';
        this.compatibleShareGroup = true;
        const { bulkActionSelectionService, folderable, setIsDraggable } = this.directive;
        const draggable = !this.isVirtualItem && !bulkActionSelectionService.bulkActionMode && folderable.item.permissions.canMove;
        setIsDraggable.bind(this.directive)(draggable);
    }
    get isVirtualItem() {
        // Virtual items are items that are shared with all ycharts users.
        // They are rendered like folderable objects, but they are not actually folderable.
        const { folderable } = this.directive;
        return !('permissions' in folderable.item);
    }
    get isValidDropTarget() {
        const { isDragging, folderable, resourceType, ancestorInfo } = this.directive;
        if (this.isVirtualItem)
            return false;
        // Cannot drop item onto itself
        if (isDragging)
            return false;
        if (resourceType === 'folder') {
            // Can drop folder item onto a folder that the user has edit permission for
            return folderable.item.permissions.canEdit && this.compatibleShareGroup;
        }
        // If dropping the item onto a file,
        // assume the user is trying to move the item into the folder that contains this file.
        // This allows moving items into a folder without scrolling to find the folder's name.
        if (ancestorInfo.id === null) {
            // Users can always move items in MyYCharts
            return true;
        }
        // We expect a folder to have a permissions block
        return ancestorInfo.permissions.canEdit && this.compatibleShareGroup;
    }
    dropEventHandler(event) {
        const { id, itemType, parentFolderId } = this.getDragEventData(event);
        if (id === null || itemType === null)
            return;
        // If the user is dropping the item on a folder, attempt to move the item into the folder.
        // If the user is dropping the item on a file,
        // assume they are trying to move an item into the file's parent folder.
        const { ancestorInfo, resourceType, itemId } = this.directive;
        const targetFolderId = resourceType === FolderableType.FOLDER ? itemId : ancestorInfo.id;
        // Do not attempt to move if the item is already in the target folder
        if (parentFolderId === targetFolderId)
            return;
        const { folderOperationsService, toastMessagesService, folderableCollectionService } = this.directive;
        folderOperationsService.moveFolderableItem(itemType, id, targetFolderId).subscribe({
            next: (response) => {
                toastMessagesService.addMessage(response.message);
                folderableCollectionService.refreshData();
            },
            error: (response) => {
                toastMessagesService.addMessage(response.error.error_message, ToastMessageType.ERROR);
            },
        });
    }
    drop(event, parentMethod) {
        const elem = this.directive.getDropTarget(event);
        elem.style.borderTop = '';
        parentMethod(event);
    }
    dragenter(event, parentMethod) {
        const elem = this.directive.getDropTarget(event);
        this.validateCompatibleShareGroups(event);
        parentMethod(event);
        this.addFileDropAreaStyles(elem);
        this.addInvalidDraggableStyles(event, elem);
    }
    dragover(event, parentMethod) {
        const elem = this.directive.getDropTarget(event);
        parentMethod(event);
        this.addFileDropAreaStyles(elem);
        this.addInvalidDraggableStyles(event, elem);
    }
    dragend(event, parentMethod) {
        const elem = this.directive.getDropTarget(event);
        elem.style.borderTop = '';
        parentMethod(event);
        sessionStorage.removeItem('draggedItemShareGroup');
        sessionStorage.removeItem('publicShareGroup');
        this.deleteDragImage();
    }
    dragleave(event, parentMethod) {
        const elem = this.directive.getDropTarget(event);
        elem.style.borderTop = '';
        parentMethod(event);
    }
    setEventData(event) {
        const { folderable, ancestorInfo, itemId, resourceType } = this.directive;
        // eslint-disable-next-line no-param-reassign
        event.dataTransfer.effectAllowed = 'move';
        // Drag data must be set in the dragstart event; cannot be set in other events
        event.dataTransfer.setData('id', `${itemId}`);
        event.dataTransfer.setData('itemType', `${resourceType}`);
        event.dataTransfer.setData('parentFolderId', `${ancestorInfo.id}`);
        event.dataTransfer.setData('shareGroup', `${folderable.item.sharing.client_group_id}`);
        const elem = this.createDragImage();
        event.dataTransfer.setDragImage(elem, 0, 0);
        // Share group is added to sessionStorage because dataTransfer is not available for access on any event but
        // `dragstart` and `drop`
        sessionStorage.setItem('draggedItemShareGroup', `${folderable.item.sharing.client_group_id}`);
        sessionStorage.setItem('publicShareGroup', folderable.item.sharing.is_client_group_public
            ? folderable.item.sharing.is_client_group_public.toString()
            : 'false');
    }
    createDragImage() {
        const { elemRef } = this.directive;
        const folderableElement = elemRef.nativeElement;
        const labelElem = folderableElement.getElementsByClassName('td file-td-name')[0];
        // Create an html element to generate a ghost image for individual drag-drop.
        // The image will be based on the element that was clicked for dragging.
        const labelContainer = labelElem.cloneNode(true);
        labelContainer.id = this.SELECTION_GHOST_IMAGE_ID;
        labelContainer.classList.add('bg-yc-blue-95', 'width-400', 'mt-10', 'mb-10', 'mr-12', 'ml-12');
        document.body.appendChild(labelContainer);
        return labelContainer;
    }
    // TODO: Add a class for the border top to simplify these blocks of code
    addFileDropAreaStyles(elem) {
        const { isValidDropTarget, positiveHighlightClass, resourceType } = this.directive;
        if (isValidDropTarget && resourceType !== FolderableType.FOLDER) {
            // Show a blue line where the moving item will be dropped
            // eslint-disable-next-line no-param-reassign
            elem.style.borderTop = 'solid 3pt #0282e8';
            elem.classList.remove(positiveHighlightClass);
        }
    }
    addInvalidDraggableStyles(event, elem) {
        const { effectAllowed } = this.getDragEventData(event);
        const { negativeHighlightClass, positiveHighlightClass, resourceType } = this.directive;
        if (effectAllowed !== 'move') {
            if (resourceType !== FolderableType.FOLDER) {
                // eslint-disable-next-line no-param-reassign
                elem.style.borderTop = 'solid 3pt #f8aaaa';
            }
            else {
                elem.classList.add(negativeHighlightClass);
                elem.classList.remove(positiveHighlightClass);
            }
        }
    }
    getDragEventData(event) {
        let id = parseInt(event.dataTransfer.getData('id'), 10);
        let parentFolderId = parseInt(event.dataTransfer.getData('parentFolderId'), 10);
        let currentShareGroup = parseInt(event.dataTransfer.getData('shareGroup') || sessionStorage.getItem('draggedItemShareGroup'), 10);
        const publicShareGroup = sessionStorage.getItem('publicShareGroup') === 'true';
        const itemType = event.dataTransfer.getData('itemType') || null;
        const { effectAllowed } = event.dataTransfer;
        if (Number.isNaN(id))
            id = null;
        if (Number.isNaN(parentFolderId))
            parentFolderId = null;
        if (Number.isNaN(currentShareGroup))
            currentShareGroup = null;
        return { id, itemType, effectAllowed, currentShareGroup, parentFolderId, publicShareGroup };
    }
    validateCompatibleShareGroups(event) {
        const { currentShareGroup, publicShareGroup } = this.getDragEventData(event);
        const { folderable } = this.directive;
        this.compatibleShareGroup =
            !currentShareGroup ||
                !folderable.item.sharing ||
                folderable.item.sharing.ancestor_client_group_ids.length === 0 ||
                folderable.item.sharing.ancestor_client_group_ids.includes(currentShareGroup) ||
                publicShareGroup;
    }
}
